import { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { createChartXAxisLabel } from "utils/charts";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useGetLoadsForecastByCommunityQuery,
  useGetOpportunityCostsByCommunityQuery,
} from "state/communities/communitiesApiSlice";

import EmsChartCard from "./EmsChartCard";

function ScenarioChart({ startDatetime, endDatetime, onDataStatus }) {
  const { t } = useTranslation(["community"]);
  const community = useSelector(selectCurrentCommunity);
  const communityId = community?.id;

  const startDate = startDatetime ? startDatetime.slice(0, 10) : undefined;
  const endDate = endDatetime ? endDatetime.slice(0, 10) : undefined;
  const hasDateFilter = !!(startDatetime || endDatetime);

  const queryOpts = { skip: !communityId, pollingInterval: 60000 };

  const { data: forecastList = [], isLoading: isLoadingForecast } =
    useGetLoadsForecastByCommunityQuery(
      { communityId, startDate, endDate, granularity: "MIN15" },
      { ...queryOpts, skip: !communityId || !startDate || !endDate }
    );

  const { data: costList = [], isLoading: isLoadingCosts } = useGetOpportunityCostsByCommunityQuery(
    {
      communityId,
      startDatetime,
      endDatetime,
      limit: hasDateFilter ? 2000 : 1000,
      offset: 0,
    },
    queryOpts
  );

  const isEmpty =
    !isLoadingForecast && !isLoadingCosts && forecastList.length === 0 && costList.length === 0;

  useEffect(() => {
    if (!isLoadingForecast && !isLoadingCosts && onDataStatus) onDataStatus(!isEmpty);
  }, [isLoadingForecast, isLoadingCosts, isEmpty, onDataStatus]);

  const { series, labels, yMin, yMax, yMinRight, yMaxRight } = useMemo(() => {
    if (forecastList.length === 0 && costList.length === 0) return { series: [], labels: [] };

    // Union of all timestamps, sorted
    const tsSet = new Set();
    forecastList.forEach((r) => r.datetime_timestamp && tsSet.add(r.datetime_timestamp));
    costList.forEach((r) => r.datetime_timestamp && tsSet.add(r.datetime_timestamp));
    const sorted = [...tsSet].sort((a, b) => new Date(a) - new Date(b));
    const lbls = sorted.map((ts) => createChartXAxisLabel(ts, "MIN15"));

    // Index by timestamp
    const consumptionByTs = new Map();
    const generationByTs = new Map();
    forecastList.forEach((r) => {
      if (!r.datetime_timestamp) return;
      if (r.consumption != null) consumptionByTs.set(r.datetime_timestamp, r.consumption);
      if (r.generation != null) generationByTs.set(r.datetime_timestamp, r.generation);
    });

    const lBuyByTs = new Map();
    const lSellByTs = new Map();
    costList.forEach((r) => {
      if (!r.datetime_timestamp) return;
      if (r.l_buy != null) lBuyByTs.set(r.datetime_timestamp, r.l_buy);
      if (r.l_sell != null) lSellByTs.set(r.datetime_timestamp, r.l_sell);
    });

    // Net forecast: consumption − generation (positive = net consumption, negative = net generation)
    const netData = sorted.map((ts) => {
      const c = consumptionByTs.get(ts);
      const g = generationByTs.get(ts);
      if (c == null && g == null) return null;
      return Number(((c ?? 0) - (g ?? 0)).toFixed(4));
    });

    const priceAt = (map) => (ts) => {
      const v = map.get(ts);
      return v != null ? Number(v.toFixed(4)) : null;
    };
    const lBuyArr = sorted.map(priceAt(lBuyByTs));
    const lSellArr = sorted.map(priceAt(lSellByTs));

    // Net load goes negative (generation surplus) while prices are always positive.
    // Align the two axes' zeros so positive prices read ABOVE the baseline and net
    // generation below it — otherwise the price lines fall into the negative-kWh
    // band and look negative. Floor/ceil to whole kWh; nice the price axis to 0.05.
    const finiteNets = netData.filter((v) => v != null);
    const priceVals = [...lBuyArr, ...lSellArr].filter((v) => v != null);
    let netMin;
    let netMax;
    let priceMin;
    let priceMax;
    if (finiteNets.length && priceVals.length) {
      netMin = Math.floor(Math.min(0, ...finiteNets));
      netMax = Math.ceil(Math.max(0, ...finiteNets));
      if (netMax === netMin) netMax += 1;
      priceMax = Math.max(0.05, Math.ceil((Math.max(...priceVals) * 1.08) / 0.05) * 0.05);
      const zf = (0 - netMin) / (netMax - netMin); // pixel fraction of the shared zero
      priceMin = zf > 0 && zf < 1 ? -priceMax * (zf / (1 - zf)) : 0;
    }

    return {
      labels: lbls,
      yMin: netMin,
      yMax: netMax,
      yMinRight: priceMin,
      yMaxRight: priceMax,
      series: [
        {
          name: t("unified_ems.scenario_chart_net_forecast"),
          type: "column",
          data: netData,
          color: "var(--info)",
        },
        {
          name: t("unified_ems.scenario_chart_l_buy"),
          type: "line",
          axis: "right",
          data: lBuyArr,
          color: "var(--accent)",
        },
        {
          name: t("unified_ems.scenario_chart_l_sell"),
          type: "line",
          axis: "right",
          data: lSellArr,
          color: "#7928CA",
        },
      ],
    };
  }, [forecastList, costList, t]);

  if (isLoadingForecast || isLoadingCosts || isEmpty) return null;

  const legend = series.map((s) => ({ label: s.name, color: s.color }));

  return (
    <EmsChartCard
      title={t("unified_ems.scenario_chart_title")}
      sub={t("unified_ems.scenario_chart_sub")}
      legend={legend}
      series={series}
      labels={labels}
      type="line"
      yTitle="kWh"
      yTitleRight="€/kWh"
      yMin={yMin}
      yMax={yMax}
      yMinRight={yMinRight}
      yMaxRight={yMaxRight}
    />
  );
}

ScenarioChart.defaultProps = {
  startDatetime: undefined,
  endDatetime: undefined,
  onDataStatus: undefined,
};

ScenarioChart.propTypes = {
  // moduleType passed by ModuleView for prop interface consistency; not used in community-wide query
  // eslint-disable-next-line react/no-unused-prop-types
  moduleType: PropTypes.string.isRequired,
  startDatetime: PropTypes.string,
  endDatetime: PropTypes.string,
  onDataStatus: PropTypes.func,
};

export default ScenarioChart;
